import { useEffect, useState } from 'react';
import {
  useCreateAddress,
  useServiceabilityAddressMutation,
  useValidateAddress,
  usePage,
} from '@vfit/consumer/data-access';
import { useQueryClient } from 'react-query';
import { CoverageErrorType } from '@vfit/shared/models';
import { addressInputIsValid, isLoggedR2, isTechFWA } from '../useCoverageTools.utils';
import { IUseCoverageToolResponse, IUseCoverageToolProps } from '../useCoverageTools.models';

export const useCoverageToolVf = ({
  product,
  offerId,
  address,
  isAddressConfirmed,
}: IUseCoverageToolProps): IUseCoverageToolResponse => {
  const queryClient = useQueryClient();
  const { products: allProducts } = usePage();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(CoverageErrorType.KO);

  const {
    data: validateAddressData,
    isSuccess: isSuccessValidateAddress,
    isError: isErrorValidateAddress,
    isLoading: isLoadingValidateAddress,
    mutate: mutateValidateAddress,
  } = useValidateAddress();
  const {
    data: serviceAbilityData,
    isSuccess: isSuccessServiceability,
    isError: isErrorServiceAbility,
    isLoading: isLoadingServiceAbility,
    mutate: mutateServiceability,
  } = useServiceabilityAddressMutation(
    'STANDARD',
    offerId,
    allProducts || [],
    product?.alternativeProductOfferList,
    product?.alternativeProductOfferListByTech,
    address?.placeId
  );
  const {
    isSuccess: isSuccessCreateAddress,
    isError: isErrorCreateAddress,
    isLoading: isLoadingCreateAddress,
    mutate: mutateCreateAddress,
  } = useCreateAddress();

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingCreateAddress) {
        setIsLoading(true);
      } else if (isErrorCreateAddress) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessCreateAddress) {
        setIsSuccess(true);
        setIsLoading(false);
        setIsError(false);
      }
    }
  }, [isLoadingCreateAddress, isErrorCreateAddress, isSuccessCreateAddress]);

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingServiceAbility) {
        setIsLoading(true);
      } else if (isErrorServiceAbility) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessServiceability) {
        if (serviceAbilityData?.serviceabilityResult?.toLowerCase() !== 'serviceable') {
          setIsLoading(false);
          setIsError(true);
          setErrorType(CoverageErrorType.NO_COVERAGE);
        } else {
          if (validateAddressData?.validAddressList?.[0]) {
            mutateCreateAddress({
              address: validateAddressData?.validAddressList?.[0],
            });
          } else {
            setIsLoading(false);
            setIsError(true);
            setErrorType(CoverageErrorType.KO);
          }
        }
      }
    }
  }, [isLoadingServiceAbility, isErrorServiceAbility, isSuccessServiceability]);

  const checkValidateAddress = () => {
    const showError = () => {
      setIsLoading(false);
      setIsError(true);
      setErrorType(isTechFWA() ? CoverageErrorType.FWA : CoverageErrorType.KO);
    };
    if (validateAddressData?.validAddressList?.[0]) {
      mutateServiceability(validateAddressData?.validAddressList?.[0]);
    } else {
      showError();
    }
  };

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingValidateAddress) {
        setIsLoading(true);
      } else if (isErrorValidateAddress) {
        setIsError(true);
      } else if (isSuccessValidateAddress) {
        checkValidateAddress();
      }
    }
  }, [isLoadingValidateAddress, isErrorValidateAddress, isSuccessValidateAddress]);

  useEffect(() => {
    if (addressInputIsValid(address) && isAddressConfirmed) {
      setIsLoading(true);
      setIsSuccess(false);
      setIsError(false);
      setErrorType(undefined);
      mutateValidateAddress({
        placeObject: address,
      });
    }
  }, [address, isAddressConfirmed]);

  useEffect(() => {
    if (isLoggedR2(queryClient)) {
      setErrorType(CoverageErrorType.R2_LOGGED);
      setIsLoading(false);
      setIsError(true);
    }
  }, []);

  return {
    isSuccess,
    isLoading,
    isError,
    errorType,
  };
};
